import { createFileRoute, redirect } from '@tanstack/react-router';
import { waitForClerk } from '../utils/clerk';
import { trpc } from '../trpc';
import { ResumeEvaluationDetails } from '../components/ResumeEvaluationDetails';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';

const VALID_ORIGIN_TABS = [
  'feed',
  'suggested-jobs',
  'applications',
  'profile',
  'analytics',
] as const;

interface SearchParams {
  from?: string;
}

export const Route = createFileRoute('/resumes_/$resumeId/evaluation')({
  component: ResumeEvaluationPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const from = (search.from as string) || 'profile';
    // Validate against allowed dashboard tabs to prevent path manipulation
    const validatedFrom = VALID_ORIGIN_TABS.includes(from as any)
      ? from
      : 'profile';
    return { from: validatedFrom };
  },
  beforeLoad: async () => {
    const user = await waitForClerk();
    if (!user) return redirect({ to: '/', replace: true });
  },
});

function ResumeEvaluationPage() {
  const { resumeId } = Route.useParams();
  const { from } = Route.useSearch();
  const navigate = Route.useNavigate();

  // Fetch current user's resume
  const { data: resume, isLoading: resumeLoading } =
    trpc.users.getResume.useQuery();

  // Fetch evaluation
  const { data: evaluation, isLoading: evaluationLoading } =
    trpc.generalResumeEvaluation.getLatest.useQuery(undefined, {
      enabled: !!resume?.id,
    });

  // Authorization check: verify resume belongs to user
  const resumeIdNumber = parseInt(resumeId, 10);
  const isInvalidId = isNaN(resumeIdNumber);
  const isUnauthorized =
    !resumeLoading && !isInvalidId && resume && resume.id !== resumeIdNumber;

  if (resumeLoading || evaluationLoading) {
    return <LoadingState />;
  }

  if (isInvalidId) {
    return <InvalidIdError />;
  }

  if (isUnauthorized) {
    return <UnauthorizedError />;
  }

  if (!resume) {
    return <NoResumeError />;
  }

  if (!evaluation) {
    return <NoEvaluationError />;
  }

  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      {/* Header with back button */}
      <div className='mb-6'>
        <Button
          variant='ghost'
          onClick={() => navigate({ to: `/dashboard/${from}` })}
          className='flex items-center gap-2 mb-4 -ml-2'
        >
          <ArrowLeft className='h-4 w-4' />
          Back to Dashboard
        </Button>
        <h1 className='text-3xl font-bold'>Resume Evaluation</h1>
        <p className='text-muted-foreground mt-2'>
          Detailed analysis of {resume.fileName}
        </p>
      </div>

      {/* Score and Summary Section */}
      <Card className='mb-6'>
        <CardContent className='pt-6'>
          {/* Overall Score Display */}
          <div className='flex items-start gap-4 mb-4'>
            <div>
              <div className='flex items-center gap-2'>
                <span className='text-4xl font-bold text-primary'>
                  {evaluation.overallScore}/10
                </span>
                <ScoreIcon score={evaluation.overallScore} />
              </div>
              <p className='text-sm text-muted-foreground mt-2'>
                Evaluated {new Date(evaluation.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Three Sentence Summary */}
          <div className='bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4'>
            <p className='text-sm text-blue-900 dark:text-blue-100'>
              {evaluation.threeSentenceSummary}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Full Evaluation Details - Always Expanded */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <ResumeEvaluationDetails evaluation={evaluation} />
        </CardContent>
      </Card>
    </div>
  );
}

// Score indicator icon
function ScoreIcon({ score }: { score: number }) {
  if (score >= 8) {
    return (
      <svg
        className='h-8 w-8 text-green-500 fill-green-500'
        viewBox='0 0 24 24'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' />
      </svg>
    );
  }
  if (score >= 6) {
    return (
      <svg
        className='h-8 w-8 text-blue-500'
        viewBox='0 0 24 24'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          fill='currentColor'
          d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'
        />
      </svg>
    );
  }
  if (score >= 4) {
    return (
      <svg
        className='h-8 w-8 text-amber-500'
        viewBox='0 0 24 24'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          fill='currentColor'
          d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'
        />
      </svg>
    );
  }
  return (
    <svg
      className='h-8 w-8 text-gray-500'
      viewBox='0 0 24 24'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        fill='currentColor'
        d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'
      />
    </svg>
  );
}

// Loading State
function LoadingState() {
  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button
        variant='ghost'
        disabled
        className='flex items-center gap-2 mb-4 -ml-2'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to Dashboard
      </Button>
      <div className='flex flex-col items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4'></div>
        <p className='text-muted-foreground'>
          Loading your resume evaluation...
        </p>
      </div>
    </div>
  );
}

// Unauthorized Error
function UnauthorizedError() {
  const navigate = Route.useNavigate();
  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button
        variant='ghost'
        onClick={() => navigate({ to: '/dashboard/profile' })}
        className='flex items-center gap-2 mb-4 -ml-2'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to Dashboard
      </Button>
      <Card className='border-red-200 bg-red-50 dark:bg-red-950'>
        <CardContent className='pt-6'>
          <div className='text-center py-8'>
            <h2 className='text-xl font-semibold text-red-900 dark:text-red-100 mb-2'>
              Access Denied
            </h2>
            <p className='text-red-800 dark:text-red-200 mb-4'>
              You don't have permission to view this resume evaluation.
            </p>
            <Button onClick={() => navigate({ to: '/dashboard/profile' })}>
              Return to Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// No Resume Error
function NoResumeError() {
  const navigate = Route.useNavigate();
  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button
        variant='ghost'
        onClick={() => navigate({ to: '/dashboard/profile' })}
        className='flex items-center gap-2 mb-4 -ml-2'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to Dashboard
      </Button>
      <Card className='border-orange-200 bg-orange-50 dark:bg-orange-950'>
        <CardContent className='pt-6'>
          <div className='text-center py-8'>
            <FileText className='h-12 w-12 text-orange-500 mx-auto mb-4' />
            <h2 className='text-xl font-semibold text-orange-900 dark:text-orange-100 mb-2'>
              No Resume Found
            </h2>
            <p className='text-orange-800 dark:text-orange-200 mb-4'>
              You haven't uploaded a resume yet. Upload one to get an
              evaluation.
            </p>
            <Button onClick={() => navigate({ to: '/dashboard/profile' })}>
              Upload Resume
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// No Evaluation Error
function NoEvaluationError() {
  const navigate = Route.useNavigate();
  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button
        variant='ghost'
        onClick={() => navigate({ to: '/dashboard/profile' })}
        className='flex items-center gap-2 mb-4 -ml-2'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to Dashboard
      </Button>
      <Card className='border-blue-200 bg-blue-50 dark:bg-blue-950'>
        <CardContent className='pt-6'>
          <div className='text-center py-8'>
            <h2 className='text-xl font-semibold text-blue-900 dark:text-blue-100 mb-2'>
              No Evaluation Yet
            </h2>
            <p className='text-blue-800 dark:text-blue-200 mb-4'>
              Your resume hasn't been evaluated yet. Start an evaluation from
              your Profile tab.
            </p>
            <Button onClick={() => navigate({ to: '/dashboard/profile' })}>
              Go to Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Invalid Resume ID Error
function InvalidIdError() {
  const navigate = Route.useNavigate();
  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button
        variant='ghost'
        onClick={() => navigate({ to: '/dashboard/profile' })}
        className='flex items-center gap-2 mb-4 -ml-2'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to Dashboard
      </Button>
      <Card className='border-red-200 bg-red-50 dark:bg-red-950'>
        <CardContent className='pt-6'>
          <div className='text-center py-8'>
            <h2 className='text-xl font-semibold text-red-900 dark:text-red-100 mb-2'>
              Invalid Resume ID
            </h2>
            <p className='text-red-800 dark:text-red-200 mb-4'>
              The resume ID in the URL is invalid. Please return to the
              dashboard and try again.
            </p>
            <Button onClick={() => navigate({ to: '/dashboard/profile' })}>
              Return to Profile
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
